import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { cn } from '@/lib/utils'
import { brandAssets } from '@/constants/assets'

interface LogoProps {
    className?: string
    /**
     * The supplied lockup is set in white with a red rule and monogram, so it
     * only reads on the dark (ink) surfaces it was drawn for. Every call site
     * in this project is on `bg-ink`; kept as a prop for API stability.
     */
    tone?: 'paper' | 'ink'
    href?: string
    /**
     * 'full' is the official lockup (monogram, wordmark and tagline), used
     * everywhere by default. 'mark' is the navbar-only variant, sized to the
     * header's compact space.
     */
    variant?: 'full' | 'mark'
}

/* Official Capital Craft Construction lockup, supplied as a single raster
   file including the "Your Home. Expertly Crafted." tagline. Used exactly as
   provided: never reconstruct, redraw or recolour it. The 'mark' variant is
   the vector wordmark supplied for the navbar, also used as-is. */
export const Logo: FC<LogoProps> = ({
    className,
    href = '/',
    variant = 'full'
}): ReactNode => {
    return (
        <Link
            href={href}
            aria-label='Capital Craft Construction, back to the home page'
            className={cn(
                'group inline-flex items-center transition-opacity hover:opacity-90',
                className
            )}
        >
            {variant === 'mark' ? (
                <img
                    src={brandAssets.logoFullSvg}
                    alt='Capital Craft Construction'
                    className='h-8 w-auto sm:h-9'
                />
            ) : (
                <img
                    src={brandAssets.logoFullPng}
                    alt='Capital Craft Construction, Your Home. Expertly Crafted.'
                    className='h-14 w-auto sm:h-16'
                />
            )}
        </Link>
    )
}
